import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api.ts";
import { useClinica } from "../clinica.ts";
import { leerCache, guardarCache } from "../cache.ts";
import { AvisoError, useAccion } from "../pantallas/comun.tsx";

const ROL: Record<string, string> = { odontologo: "Odontólogo/a", especialista: "Especialista", laboratorio: "Laboratorio dental" };
const fecha = (iso: string) => new Date(iso).toLocaleDateString("es-EC", { day: "numeric", month: "short", year: "numeric", timeZone: "America/Guayaquil" });

/** Invitaciones de otras clínicas para quien inició sesión: se aceptan o se rechazan aquí (nadie puede sumarte a una clínica sin que aceptes). */
export function InvitacionesRecibidas() {
  const [lista, setLista] = useState<{ id: string; clinica: string; rol: string; sede: string | null; vence: string }[]>(() => leerCache<{ id: string; clinica: string; rol: string; sede: string | null; vence: string }[]>("inicio.invitaciones") ?? []);
  const [ok, setOk] = useState("");
  const a = useAccion();
  const cargar = () => api<typeof lista>("GET", "/api/mis-invitaciones").then((l) => setLista(guardarCache("inicio.invitaciones", l))).catch(() => setLista([]));
  useEffect(() => { cargar(); }, []);
  async function responder(id: string, aceptar: boolean, clinica: string) {
    const r = await a.correr(() => api("POST", `/api/mis-invitaciones/${id}/${aceptar ? "aceptar" : "rechazar"}`));
    if (!r) return;
    const lab = lista.find((x) => x.id === id)?.rol === "laboratorio";
    setOk(aceptar ? (lab ? `Aceptaste: las órdenes de ${clinica} aparecerán en tu bandeja de entrada.` : `Aceptaste: ya trabajas también en ${clinica}. Cambia de clínica con el selector de arriba.`) : "Invitación rechazada.");
    cargar();
    if (aceptar) dispatchEvent(new Event("donna:clinicas"));
  }
  if (!lista.length && !ok) return null;
  return (
    <section className="card" aria-labelledby="t-inv">
      <h2 id="t-inv">Invitaciones de clínicas</h2>
      {ok && <div className="aviso ok" role="status">{ok}</div>}
      {lista.map((i) => (
        <div key={i.id} className="fila" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
          <span><b>{i.clinica}</b> te invita a trabajar como {ROL[i.rol] ?? i.rol}{i.sede ? ` en la sede ${i.sede}` : ""}. <span className="muted" style={{ fontSize: 13 }}>Vence el {fecha(i.vence)}.</span></span>
          <span className="fila">
            <button className="btn chico pri" disabled={a.ocupado} onClick={() => responder(i.id, true, i.clinica)}>Aceptar</button>
            <button className="btn chico" disabled={a.ocupado} onClick={() => responder(i.id, false, i.clinica)}>Rechazar</button>
          </span>
        </div>
      ))}
      {lista.length > 0 && <small className="muted">Si aceptas, esa clínica te agrega a su equipo (cuenta como un usuario de su plan) y podrás cambiar de clínica sin escribir otra contraseña. Cada clínica solo ve a sus propios pacientes.</small>}
      <AvisoError error={a.error} />
    </section>
  );
}

/** Para la administración: invitar a un profesional que ya usa Donna en otra clínica. */
export function InvitarProfesional() {
  const sedes = (useClinica()?.sedes ?? []).filter((x) => x.activa);
  const [email, setEmail] = useState("");
  const [rol, setRol] = useState("odontologo");
  const [sedeId, setSedeId] = useState("");
  const [msg, setMsg] = useState("");
  const [lista, setLista] = useState<{ id: string; email: string; rol: string; estado: string; vence: string; sede: string | null; vencida: boolean }[]>([]);
  const a = useAccion();
  const cargar = () => api<typeof lista>("GET", "/api/invitaciones").then(setLista).catch(() => undefined);
  useEffect(() => { cargar(); }, []);
  async function enviar(e: FormEvent) {
    e.preventDefault(); setMsg("");
    const r = await a.correr(() => api<{ mensaje: string }>("POST", "/api/invitaciones", { email, rol, ...(sedeId ? { sedeId } : {}) }));
    if (r) { setMsg(r.mensaje); setEmail(""); cargar(); }
  }
  async function cancelar(id: string) {
    if (await a.correr(() => api("POST", `/api/invitaciones/${id}/cancelar`))) cargar();
  }
  const ESTADO: Record<string, string> = { pendiente: "Pendiente", aceptada: "Aceptada", rechazada: "Rechazada", cancelada: "Cancelada" };
  return (
    <>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        ¿Un odontólogo o especialista que ya usa Donna en otra clínica va a trabajar también en la tuya? Invítalo con su correo. Él acepta desde su propia cuenta y cuenta como un usuario de tu plan.
        Tu clínica solo ve a sus propios pacientes y la agenda de tu clínica; Donna solo avisa al profesional si ya está ocupado en otra.
      </p>
      <form className="fila" onSubmit={enviar} aria-label="Invitar a un profesional" style={{ alignItems: "end", flexWrap: "wrap" }}>
        <label className="campo" style={{ flex: "1 1 240px" }}>Correo del profesional<input className="entrada" type="email" required value={email} onChange={(e) => { setEmail(e.target.value); setMsg(""); }} placeholder="nombre@correo.com" /></label>
        <label className="campo">Rol en tu clínica<select className="entrada" value={rol} onChange={(e) => setRol(e.target.value)}>{Object.entries(ROL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        {sedes.length > 1 && <label className="campo">Sede<select className="entrada" value={sedeId} onChange={(e) => setSedeId(e.target.value)}><option value="">Sin sede asignada</option>{sedes.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}</select></label>}
        <button className="btn pri" disabled={a.ocupado}>Enviar invitación</button>
      </form>
      {msg && <div className="aviso ok" role="status">{msg}</div>}
      <AvisoError error={a.error} />
      {lista.length > 0 && (
        <div className="tabla-scroll">
          <table className="tabla-resp" aria-label="Invitaciones enviadas">
            <thead><tr><th>Correo</th><th>Rol</th><th>Estado</th><th>Vence</th><th></th></tr></thead>
            <tbody>{lista.map((i) => (
              <tr key={i.id}>
                <td className="mono" style={{ fontSize: 13 }}>{i.email}</td><td>{ROL[i.rol] ?? i.rol}{i.sede ? ` · ${i.sede}` : ""}</td>
                <td><span className={`chip ${i.estado === "aceptada" ? "e-confirmada" : ""}`}>{i.vencida ? "Vencida" : ESTADO[i.estado] ?? i.estado}</span></td>
                <td style={{ fontSize: 13 }}>{fecha(i.vence)}</td>
                <td className="acciones-fila">{i.estado === "pendiente" && !i.vencida && <button className="btn chico" disabled={a.ocupado} onClick={() => cancelar(i.id)}>Cancelar</button>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** Invitaciones de laboratorios que quieren conectarse con la clínica (las ve la administración en Inicio). */
export function InvitacionesLaboratorio() {
  const [lista, setLista] = useState<{ id: string; laboratorio: string; vence: string }[]>([]);
  const [ok, setOk] = useState("");
  const a = useAccion();
  const cargar = () => api<typeof lista>("GET", "/api/laboratorio/invitaciones-recibidas").then(setLista).catch(() => setLista([]));
  useEffect(() => { cargar(); }, []);
  async function responder(id: string, aceptar: boolean, nombre: string) {
    const r = await a.correr(() => api("POST", `/api/laboratorio/invitaciones-recibidas/${id}/${aceptar ? "aceptar" : "rechazar"}`));
    if (!r) return;
    setOk(aceptar ? `Listo: ${nombre} quedó conectado. Ya puedes enviarle órdenes desde Laboratorio.` : "Invitación rechazada.");
    cargar();
  }
  if (!lista.length && !ok) return null;
  return (
    <section className="card" aria-labelledby="t-invlab">
      <h2 id="t-invlab">Laboratorios que quieren conectarse</h2>
      {ok && <div className="aviso ok" role="status">{ok}</div>}
      {lista.map((i) => (
        <div key={i.id} className="fila" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
          <span><b>{i.laboratorio}</b> te invita a recibir y enviar órdenes de trabajo por Donna. <span className="muted" style={{ fontSize: 13 }}>Vence el {fecha(i.vence)}.</span></span>
          <span className="fila">
            <button className="btn chico pri" disabled={a.ocupado} onClick={() => responder(i.id, true, i.laboratorio)}>Aceptar</button>
            <button className="btn chico" disabled={a.ocupado} onClick={() => responder(i.id, false, i.laboratorio)}>Rechazar</button>
          </span>
        </div>
      ))}
      <small className="muted">El laboratorio verá solo el nombre del paciente y los datos de cada orden que le envíes; nada más de tu clínica. Requiere el plan Clínica o Total.</small>
      <AvisoError error={a.error} />
    </section>
  );
}
